import { Card, CardContent, CardHeader, CardTitle } from '@/components/card';
import { TrendingUp } from 'lucide-react';

import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const BASE_CLASS = 'mm-stats';

const rootVariants = cva(`${BASE_CLASS} relative shadow-sm p-0`, {
  variants: {
    variant: {
      default: 'text-primary',
      primary:
        'bg-primary text-primary-foreground border-none **:data-[slot=stats-card-icon]:bg-primary-foreground/10',
      outline: 'bg-transparent',
      secondary:
        'bg-secondary text-secondary-foreground **:data-[slot=stats-card-icon]:bg-secondary-foreground/10',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export type StatsCardProps = React.ComponentProps<'div'> &
  VariantProps<typeof rootVariants> & {
    label?: React.ReactNode;
    description?: React.ReactNode;
    value?: React.ReactNode;
    change?: React.ReactNode;
    trending?: 'up' | 'down';
    icon?: React.ElementType;
  };

export const StatsCard = ({
  label,
  description,
  value,
  change,
  icon: Icon = TrendingUp,
  trending,
  variant = 'default',
  className,
  ...others
}: StatsCardProps) => {
  return (
    <Card
      {...others}
      data-slot="stats-card"
      className={cn(rootVariants({ variant, className }))}
      data-trending={trending}
    >
      <CardHeader
        className={cn(
          'flex flex-row items-center justify-between space-y-0 p-3 pb-0',
          { 'pb-2': Boolean(description) }
        )}
      >
        <CardTitle className="text-sm font-medium">
          {label}
          {description && (
            <p className="text-xs text-muted-foreground">{description}</p>
          )}
        </CardTitle>
        <StatsCardIcon>
          <Icon className="size-5" />
        </StatsCardIcon>
      </CardHeader>

      <CardContent className="p-3 pt-0">
        <div className="flex gap-3 items-baseline">
          <span className="text-2xl font-bold">{value}</span>
          <span className="text-xs text-muted-foreground in-data-[trending=up]:text-success in-data-[trending=down]:text-destructive font-medium">
            {change}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

export const StatsCardIcon = ({
  className,
  ...others
}: React.ComponentProps<'div'>) => (
  <div
    data-slot="stats-card-icon"
    className={cn('p-2 rounded-lg bg-card-foreground/10', className)}
    {...others}
  />
);
